import { Component, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { SummaryCardAction, SummaryCardComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { SeriesOptionsType } from 'highcharts';

import { combineLatest, map } from 'rxjs';

import { filter } from 'rxjs/operators';

import {
  OrganicMatterBalanceState,
  OrganicMatterBenchmark,
  OrganicMatterReport,
} from '../../organic-matter-balance.models';
import { loadOrganicMatterBalance, loadOrganicMatterBenchmark } from '../../store/organic-matter-balance.actions';
import {
  OrganicMatterBenchmarkSelectors,
  OrganicMatterReportSelectors,
} from '../../store/organic-matter-balance.selectors';
import { OrganicMatterReportDialogComponent } from '../report/organic-matter-report-dialog.component';
import { selectCropYear } from '../../../../../store/selectors';
import { SkeletonComponent } from '@next/arable-lib/external-resource';
import { EmptyWidgetComponent } from '../../../../../shared/components/empty-widget.component';
import { ChartComponent } from '@next/core-lib/chart';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/divider';
import { MatButton } from '@angular/material/button';
import { AsyncPipe, DecimalPipe } from '@angular/common';

@Component({
    selector: 'app-widget-organic-matter-balance',
    templateUrl: './organic-matter-balance.component.html',
    styleUrls: ['./organic-matter-balance.component.scss'],
    imports: [
        SummaryCardComponent,
        SkeletonComponent,
        EmptyWidgetComponent,
        ChartComponent,
        MatIcon,
        MatTooltip,
        MatDivider,
        MatButton,
        NotificationEmptyComponent,
        AsyncPipe,
        DecimalPipe,
        TranslatePipe,
    ],
})
export class OrganicMatterBalanceComponent implements OnInit {
  private readonly store = inject<Store<OrganicMatterBalanceState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  readonly cropYear = toSignal(this.store.select(selectCropYear), {
    initialValue: new Date().getFullYear(),
  });

  customer$ = this.store.select(UserSelectors.selectCustomer);
  companyName$ = this.customer$.pipe(
    map((customer) => customer?.name || ''),
  );

  isLoading$ = combineLatest([
    this.store.select(OrganicMatterReportSelectors.selectIsLoading),
    this.store.select(OrganicMatterBenchmarkSelectors.selectIsLoading),
  ]).pipe(
    map(([reportLoading, benchmarkLoading]) => reportLoading || benchmarkLoading),
  );

  showError$ = combineLatest([
    this.store.select(OrganicMatterReportSelectors.selectIsError),
    this.store.select(OrganicMatterBenchmarkSelectors.selectIsError),
  ]).pipe(
    map(([reportError, benchmarkError]) => reportError || benchmarkError),
  );

  organicMatterReport$ = this.store.select(OrganicMatterReportSelectors.selectData)
  organicMatterBenchmark$ = this.store.select(OrganicMatterBenchmarkSelectors.selectData);

  dataSource$ = combineLatest([
    this.organicMatterReport$,
    this.organicMatterBenchmark$,
    this.companyName$,
  ]).pipe(
    filter(([report, benchmark]) => !!report && !!benchmark),
    map(([report, benchmark, companyName]) => this.createDataSource(report, benchmark, companyName)),
  );

  chartOptions$ = combineLatest([
    this.organicMatterReport$,
    this.organicMatterBenchmark$,
  ]).pipe(
    filter(([report, benchmark]) => !!report && !!benchmark),
    map(([report, benchmark]) => this.createChartOptions(report, benchmark)),
  );

  informationTooltipText = this.locale.translate('organicMatterBenchmarkTooltip');
  cardActions: SummaryCardAction[] = [
    {
      id: 'info',
      icon: 'info_outline',
      callback: () => this.openInformationDialog(),
    },
  ];

  ngOnInit() {
    this.store.dispatch(loadOrganicMatterBalance({ cropYear: this.cropYear() }));
    this.store.dispatch(loadOrganicMatterBenchmark({ cropYear: this.cropYear() }));
  }

  prepareScore = (score: number) => (score ? Math.round(score) : 0);

  createDataSource(report: OrganicMatterReport, benchMark: OrganicMatterBenchmark, companyName: string): SeriesOptionsType[] {
    return [
      {
        type: 'column',
        name: `${companyName} ${this.locale.translate('organicMatterValue')}`,
        color: '#B2DF8A',
        data: [report.totals?.organicMatterBalancePerHectare ?? 0],
      },
      {
        type: 'column',
        name: this.locale.translate('benchmark'),
        color: '#6B6B6B',
        data: [this.prepareScore(Math.round(benchMark.kgOMPerHa))],
      },
    ];
  }

  createChartOptions(report: OrganicMatterReport, benchMark: OrganicMatterBenchmark): Highcharts.Options {
    return {
      chart: {
        height: '250px',
      },
      xAxis: {
        categories: [
          `${Math.round(report.totals?.organicMatterBalancePerHectare)}`,
          `${this.prepareScore(Math.round(benchMark.kgOMPerHa))}`,
        ],
      },
      yAxis: {
        title: {
          text: null,
        },
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
    };
  }

  openInformationDialog(): void {
    this.ai.trackEvent('OrganicMatterBalance.openInformationDialog', {});
    this.dialog.notify(
      {
        title: this.locale.translate('organicMatterBalanceInfoTitle'),
        text: this.locale.translate('organicMatterBalanceInfoText'),
      },
      {
        maxWidth: '600px',
      },
    );
  }

  openOrganicMatterReportDialog(report: OrganicMatterReport, companyName: string | null = ''): void {
    this.ai.trackEvent('OrganicMatterBalance.openOrganicMatterReportDialog', {});
    this.dialog.form(OrganicMatterReportDialogComponent, {
      report: report,
       title: `${companyName} - ${this.locale.translate('organicMatterReportTitle')} ${this.cropYear()}`,
      fullscreen: true,
      confirmButton: false,
      pushHistoryState: false,
      cancelButtonText: this.locale.translate('close'),
    });
  }
}
